/**
 * Blademaster tier set: Fangreaver's Battlegear (crafting.md §5.4). `bonuses` target the
 * rotation-rework kit (docs/design/classes/blademaster.md); `interim` uses today's kit.
 * The 2pc needs `minStacks` on `targetHasAura` (sim contract, owned by the rotation rework), so
 * it is declared in `needs` and falls back to the interim 2pc until that lands.
 */
import { ABILITY, AURA, CLASS } from '@keystone/content';
import type { Condition } from '@keystone/sim';
import { TIER_SET_IDS, tierAdd, tierMult } from '../../forge.constants';
import { auraRef } from '../../transforms';
import type { TierSetDef } from '../../types';

const B = ABILITY.blademaster;
const RW = {
  wounds: 'blademaster.wounds',
  suddenDeath: 'blademaster.sudden_death',
} as const;

/** "Target has 3+ of your Wounds" (the APL casts Mortal Strike at ≥ 3; needs the sim's targetHasAura.minStacks). */
const threeWounds: Condition = { type: 'targetHasAura', auraId: RW.wounds, fromSelf: true, minStacks: 3 };

export const BLADEMASTER_SET: TierSetDef = {
  id: TIER_SET_IDS[CLASS.blademaster]!,
  name: "Fangreaver's Battlegear",
  classId: CLASS.blademaster,
  pieceNames: { helm: 'Fangreaver Visage', chest: 'Fangreaver Hauberk', legs: 'Fangreaver Legguards', gloves: 'Fangreaver Grips', boots: 'Fangreaver Treads' },
  text: {
    2: { name: 'Open Veins', description: 'Mortal Strike on 3 or more Wounds refunds 10 Rage and grants Tactician.' },
    4: { name: 'Red Harvest', description: 'Sudden Death Executes deal 115% more damage and have a 50% chance to reset Mortal Strike. Colossus Smash grants Sudden Death.' },
  },
  needs: { 2: ['sim:targetHasAura.minStacks'] },
  bonuses: {
    2: [
      {
        op: 'addEffects',
        abilityId: B.mortalStrike,
        position: 'start',
        effects: [
          {
            type: 'conditional',
            cond: threeWounds,
            then: [
              { type: 'resource', to: 'self', resource: 'rage', amount: Math.round(tierAdd(10)) },
              { type: 'applyAura', to: 'self', aura: auraRef(AURA.overpowerProc) },
            ],
          },
        ],
      },
    ],
    4: [
      { op: 'addAuraMods', auraId: RW.suddenDeath, mods: [{ stat: 'damageDonePct', value: tierAdd(1.15), abilityIds: [B.execute] }] },
      {
        op: 'addEffects',
        abilityId: B.execute,
        position: 'start',
        effects: [
          {
            type: 'conditional',
            cond: { type: 'selfHasAura', auraId: RW.suddenDeath },
            then: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.5) }, then: [{ type: 'modifyCooldown', abilityId: B.mortalStrike, reset: true }] }],
          },
        ],
      },
      { op: 'addEffects', abilityId: B.colossusSmash, effects: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.suddenDeath) }] },
    ],
  },
  interim: {
    text: {
      2: { name: 'Open Veins', description: 'Mortal Strike refunds 10 Rage and grants Tactician.' },
      4: { name: 'Red Harvest', description: 'Colossus Smash resets Mortal Strike. Execute deals 30% more damage and has a 50% chance to reset Mortal Strike.' },
    },
    bonuses: {
      2: [
        {
          op: 'addEffects',
          abilityId: B.mortalStrike,
          effects: [
            { type: 'resource', to: 'self', resource: 'rage', amount: Math.round(tierAdd(10)) },
            { type: 'applyAura', to: 'self', aura: auraRef(AURA.overpowerProc) },
          ],
        },
      ],
      4: [
        { op: 'addEffects', abilityId: B.colossusSmash, effects: [{ type: 'modifyCooldown', abilityId: B.mortalStrike, reset: true }] },
        { op: 'scaleAmount', abilityId: B.execute, effectType: 'damage', mult: tierMult(1.3) },
        {
          op: 'addEffects',
          abilityId: B.execute,
          effects: [{ type: 'conditional', cond: { type: 'chance', p: tierAdd(0.5) }, then: [{ type: 'modifyCooldown', abilityId: B.mortalStrike, reset: true }] }],
        },
      ],
    },
  },
};
